import {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import { mapSweepstakesData } from '../components/Sweepstakes/utils/SweepstakesDataMapper';
import { useFetchChallengesInfo } from './fetch-challenges';
import { getApiHost } from '../common/utils/ApiUtils';

const getMetaValue = (meta = [], key) => {
  if (!meta) return undefined;

  // Handle array format (legacy)
  if (Array.isArray(meta)) {
    const entry = meta.find((item) => item?.key === key);
    return entry?.value;
  }

  // Handle object format
  if (typeof meta === 'object' && meta !== null) {
    return meta[key];
  }

  // Handle string format (potential JSON from Map scalar)
  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta);
      return parsed[key];
    } catch {
      return undefined;
    }
  }

  return undefined;
};

const normalizeSweepstakesChallenge = (challenge) => {
  if (!challenge) return null;

  const challengeId = challenge.challengeId || challenge.id;
  const challengeEndDate = challenge.challengeEndDate || challenge.endDate;
  const tasksFromChallengeSteps = Array.isArray(challenge.challengeSteps)
    ? challenge.challengeSteps.map((step) => ({
      customerTaskId: step.customerTaskId,
      name: step.name,
      description: step.description,
      sequenceNumber: step.sequenceNumber,
      status: step.completed ? 'COMPLETED' : 'NEW',
    }))
    : null;

  const tasks = Array.isArray(challenge.tasks)
    ? challenge.tasks.map((task) => ({
      ...task,
      customerTaskId: task.customerTaskId || task.id,
    }))
    : tasksFromChallengeSteps;

  return {
    ...challenge,
    challengeId,
    challengeEndDate,
    tasks,
  };
};

const isObjectEmpty = (value) => {
  return Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length === 0,
  );
};

const getProvidedChallengesList = (providedData) => {
  const directList = providedData?.challenges;
  if (Array.isArray(directList)) return directList;

  const offeredChallengesList = providedData?.offeredChallenges?.challenges;
  if (Array.isArray(offeredChallengesList)) return offeredChallengesList;

  const nestedOfferedChallengesList = providedData?.data?.offeredChallenges?.challenges;
  if (Array.isArray(nestedOfferedChallengesList)) return nestedOfferedChallengesList;

  return null;
};

const selectSweepstakesChallenge = (challenges = []) => {
  const sweepstakesChallenges = challenges.filter((challenge) => {
    const type = getMetaValue(challenge?.challengeMetaData, 'type');
    return type === 'SWEEPSTAKES' || type === 'sweepstakes';
  });

  const activeSweepstakes = sweepstakesChallenges.find(
    (challenge) => challenge?.challengeStatus === 'ACTIVE',
  );

  return activeSweepstakes || sweepstakesChallenges[0] || challenges[0] || null;
};

const processChallengesList = (challenges) => {
  const selectedChallenge = selectSweepstakesChallenge(challenges);
  const normalized = normalizeSweepstakesChallenge(selectedChallenge);

  return {
    challengeData: normalized ? mapSweepstakesData(normalized) : null,
    hasNoChallengeData: !normalized,
  };
};

const processProvidedData = (providedData) => {
  if (providedData?.challengeEnded || isObjectEmpty(providedData)) {
    return { challengeData: null, userProfileData: null, hasNoChallengeData: true };
  }

  const providedChallengesList = getProvidedChallengesList(providedData);
  if (Array.isArray(providedChallengesList)) {
    const { challengeData, hasNoChallengeData } = processChallengesList(providedChallengesList);
    return { challengeData, userProfileData: null, hasNoChallengeData };
  }

  const isAlreadyMapped = !providedData?.challengeId;
  const finalData = isAlreadyMapped ? providedData : mapSweepstakesData(providedData);
  const isMappedDataEmpty = isObjectEmpty(finalData);

  return {
    challengeData: isMappedDataEmpty ? null : finalData,
    userProfileData: null,
    hasNoChallengeData: !finalData || isMappedDataEmpty,
  };
};

/*
  New function acceptSweepstakesFromAPI
  Description: Called from useFetchSweepstakesAccept to accept sweepstakes via REST API.
    Body properties come from the sweepstakes data
  Method: PUT
  Endpoint: /b2b/user/account/{userID}/customer/{svocID}/challenges/accept
  Body: {
    acceptChallengeRequest: {
      "challengeId": "<challengeId>",
      "customerChallengeId": "<customerChallengeId>" // optional, undefined if not available
      "isV2": "<v2Challenge>" // optional, true/false, false if not available
    }
  }
  Headers: channelId: '1', 'Content-Type': 'application/json'
*/

const acceptSweepstakesFromAPI = async ({ challengeId, customerChallengeId, isV2 } = {}) => {
  // Get THDCustomer object from cookie or window object
  const getTHDCustomer = () => {
    try {
      // Try to get from window.THDCustomer first (if available)
      if (window.THDCustomer) {
        return window.THDCustomer.default;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const thdCustomer = getTHDCustomer();

  if (!thdCustomer) {
    throw new Error('THDCustomer not found');
  }

  const { userID, svocID } = thdCustomer;
  const apiHost = getApiHost();

  // Construct API URL
  const endpoint = 'challenges/accept';
  const apiUrl = `${apiHost}/b2b/user/account/${userID}/customer/${svocID}/${endpoint}`;

  const body = {
    acceptChallengeRequest: {
      challengeId,
      ...(customerChallengeId && { customerChallengeId }),
      ...(typeof isV2 === 'boolean' && { isV2 }),
    },
  };

  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      channelId: '1',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include', // Include cookies
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    return { success: true };
  }
};

export const useFetchSweepstakes = ({
  providedData,
  userOptInStatus = 'notOptedIn',
  skip = false,
  useRestAPI = false,
} = {}) => {
  const shouldSkipGraphql = Boolean(skip || providedData);

  const { isLoadingChallenges,
    hasErrorChallenges,
    challengesData,
    refreshChallengeData } = useFetchChallengesInfo({ challengeType: 'sweepstakes', skip: shouldSkipGraphql });

  const { challengeData, userProfileData, hasNoChallengeData } = useMemo(() => {
    if (providedData) {
      return processProvidedData(providedData);
    }

    const list = challengesData?.challenges;
    let challenges = [];
    if (Array.isArray(list)) {
      challenges = list;
    } else if (list) {
      challenges = [list];
    }
    const { challengeData: resolved, hasNoChallengeData: noData } = processChallengesList(challenges);

    return {
      challengeData: resolved,
      userProfileData: challengesData?.userProfile || null,
      hasNoChallengeData: noData,
    };
  }, [challengesData, providedData]);

  const isLoading = skip ? false : !providedData && isLoadingChallenges;
  const hasError = skip ? false : !providedData && Boolean(hasErrorChallenges);
  const isChallengeEnded = (() => {
    if (skip) return false;
    if (providedData?.challengeEnded) return true;
    return !isLoading && !hasError && (hasNoChallengeData || challengeData === null);
  })();

  const refresh = useCallback(() => {
    if (!providedData && !skip) {
      refreshChallengeData();
    }
  }, [providedData, refreshChallengeData, skip]);

  return {
    challengeData,
    userProfileData,
    isLoading,
    hasError,
    isChallengeEnded,
    refresh,
  };
};

// TODO: flag for removal since replacing with v2 gql acceptChallenge endpoint
export const useFetchSweepstakesAccept = ({ forceError = false, useRestAPI = false } = {}) => {
  const [acceptData, setAcceptData] = useState(null);
  const [isLoadingAccept, setIsLoadingAccept] = useState(false);
  const [acceptError, setAcceptError] = useState(false);

  const acceptSweepstakes = useCallback(
    async ({ challengeId, customerChallengeId, isV2 } = {}) => {
      setIsLoadingAccept(true);
      setAcceptError(false);

      if (useRestAPI) {
        try {
          const data = await acceptSweepstakesFromAPI({ challengeId, customerChallengeId, isV2 });
          const responseData = {
            success: true,
            message: 'Successfully opted into sweepstakes',
            data,
          };
          setAcceptData(responseData);
          return responseData;
        } catch (error) {
          setAcceptError(true);
          const errorResponse = { success: false, error: error.message || 'API error occurred' };
          setAcceptData(errorResponse);
          return errorResponse;
        } finally {
          setIsLoadingAccept(false);
        }
      }

      try {
        // Simulate accepting sweepstakes
        await new Promise((resolve, reject) => {
          setTimeout(() => {
            if (forceError) {
              reject(new Error('Simulated network error for testing'));
            } else {
              resolve();
            }
          }, 1000);
        });

        const responseData = {
          success: true,
          message: 'Successfully opted into sweepstakes',
        };

        setAcceptData(responseData);
        return responseData;
      } catch (error) {
        setAcceptError(true);
        const errorResponse = { success: false, error: error.message || 'Network error occurred' };
        setAcceptData(errorResponse);
        return errorResponse;
      } finally {
        setIsLoadingAccept(false);
      }
    },
    [forceError, useRestAPI],
  );

  return {
    acceptData,
    isLoadingAccept,
    acceptError,
    acceptSweepstakes,
  };
};

export const useUpdateSweepstakes = (baseData) => {
  const [localDataUpdates, setLocalDataUpdates] = useState(null);
  const baseDataRef = useRef(baseData);

  // Keep ref in sync with baseData
  useEffect(() => {
    baseDataRef.current = baseData;
  }, [baseData]);

  // Apply local updates to base data
  const effectiveData = localDataUpdates ? { ...baseData, ...localDataUpdates } : baseData;

  const stepUpdater = (step, updates) => {
    const stepUpdate = updates.steps.find((updateStep) => updateStep.id === step.id);
    return stepUpdate ? { ...step, ...stepUpdate } : step;
  };

  // Function to update sweepstakes data at runtime
  const updateSweepstakesData = useCallback((updates) => {
    setLocalDataUpdates((prevUpdates) => {
      const currentBaseData = baseDataRef.current;
      const currentData = prevUpdates ? { ...currentBaseData, ...prevUpdates } : currentBaseData;
      const updatedData = { ...currentData };
      const processStep = (step) => stepUpdater(step, updates);

      // Apply all updates dynamically
      Object.keys(updates).forEach((key) => {
        if (key === 'completedSteps') {
          // Special handling for completedSteps to ensure it only increases
          updatedData.completedSteps = Math.max(
            updatedData.completedSteps || 0,
            updates.completedSteps,
          );
        } else if (key === 'steps' && updates.steps && updatedData.steps) {
          // Special handling for steps array
          updatedData.steps = updatedData.steps.map(processStep);
        } else {
          // Direct assignment for all other fields (ctaTitle, isOptedIn, status, etc.)
          updatedData[key] = updates[key];
        }
      });

      // Return only the updates (delta from base data)
      const deltaUpdates = {};
      Object.keys(updatedData).forEach((key) => {
        if (JSON.stringify(updatedData[key]) !== JSON.stringify(currentBaseData?.[key])) {
          deltaUpdates[key] = updatedData[key];
        }
      });

      return Object.keys(deltaUpdates).length > 0 ? deltaUpdates : null;
    });
  }, []);

  // Reset updates when base data changes
  useEffect(() => {
    setLocalDataUpdates(null);
  }, [baseData]);

  return {
    effectiveData,
    updateSweepstakesData,
  };
};
